import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import { notify } from '../../components/common/AppToast';
import KkpDistribMap from '../../components/kkp/KkpDistribMap';
import KkpDistribPointModal from '../../components/kkp/KkpDistribPointModal';
import kkpDistribService from '../../services/kkpDistribService';
import { AppBadge, AppButton } from '../../components/ui';
import '../../styles/kkp-planning-page.scss';
import '../../styles/kkp-distrib-page.scss';

const hasCoords = (point) => point.latitude != null && point.longitude != null;

const formatAddress = (point) => {
  const line = [point.adresse, [point.code_postal, point.ville].filter(Boolean).join(' ')].filter(Boolean).join(' · ');
  return line || 'Adresse non renseignée';
};

const notifyGeocodeResult = (result) => {
  const unresolved = [...(result.failed_points ?? []), ...(result.skipped_points ?? [])];
  const names = unresolved.map((p) => p.nom).filter(Boolean);
  if (result.geocoded > 0 && names.length === 0) {
    notify.success(`${result.geocoded} adresse(s) placée(s).`);
    return;
  }
  if (result.geocoded > 0) {
    notify.success(`${result.geocoded} adresse(s) placée(s).`);
  }
  if (names.length) {
    const preview = names.slice(0, 4).join(', ');
    const extra = names.length > 4 ? ` (+${names.length - 4})` : '';
    notify.error(`Sans coordonnées : ${preview}${extra}`);
  } else if (result.remaining > 0) {
    notify.error(`${result.remaining} point(s) sans coordonnées. Voir la liste à droite.`);
  }
};

const KkpDistribCartePage = () => {
  const [zones, setZones] = useState([]);
  const [points, setPoints] = useState([]);
  const [activeZones, setActiveZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geocoding, setGeocoding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingModal, setSavingModal] = useState(false);

  const load = useCallback(async (keepSelection = false) => {
    setLoading(true);
    try {
      const [meta, listRes] = await Promise.all([
        kkpDistribService.getMeta(),
        kkpDistribService.listPoints({ limit: 1000 }),
      ]);
      const nextZones = meta?.zones ?? [];
      setZones(nextZones);
      setPoints(listRes.points);
      setActiveZones((prev) => {
        if (keepSelection && prev.length) {
          const codes = new Set(nextZones.map((z) => z.code || z.key));
          return prev.filter((code) => codes.has(code));
        }
        return nextZones.map((z) => z.code || z.key);
      });
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const zoneColors = useMemo(
    () => Object.fromEntries(zones.map((z) => [z.code || z.key, z.couleur])),
    [zones],
  );

  const visiblePoints = useMemo(
    () => points.filter((p) => activeZones.includes(p.zone)),
    [points, activeZones],
  );

  const missingPoints = useMemo(
    () => visiblePoints.filter((p) => !hasCoords(p)),
    [visiblePoints],
  );

  const geocodedCount = visiblePoints.length - missingPoints.length;

  const toggleZone = (code) => {
    setActiveZones((prev) => (
      prev.includes(code) ? prev.filter((z) => z !== code) : [...prev, code]
    ));
  };

  const handleGeocode = async () => {
    setGeocoding(true);
    try {
      const zone = activeZones.length === 1 ? activeZones[0] : '';
      const result = await kkpDistribService.geocode({ zone, limit: 80 });
      notifyGeocodeResult(result);
      await load(true);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setGeocoding(false);
    }
  };

  const handleSaveModal = async (payload) => {
    if (!editing?.id) return;
    setSavingModal(true);
    try {
      await kkpDistribService.updatePoint(editing.id, payload);
      notify.success('Point mis à jour');
      setEditing(null);
      await load(true);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSavingModal(false);
    }
  };

  return (
    <MasterLayout>
      <Breadcrumb
        title="Carte des points de dépôt"
        subtitle="OpenStreetMap · filtrez les zones. L’itinéraire viendra ensuite."
        actions={(
          <div className="d-flex gap-2">
            <AppButton
              variant="outline-secondary"
              icon="mdi:routes"
              disabled
              title="Fonctionnalité à venir"
            >
              Créer un itinéraire
            </AppButton>
            <AppButton
              icon="mdi:map-marker-plus-outline"
              onClick={handleGeocode}
              disabled={geocoding || missingPoints.length === 0}
              loading={geocoding}
              loadingLabel="Géocodage…"
            >
              Placer les adresses
            </AppButton>
          </div>
        )}
      />

      <div className="kkp-distrib-map-toolbar mb-3">
        {zones.map((zone) => {
          const code = zone.code || zone.key;
          const active = activeZones.includes(code);
          return (
            <button
              key={code}
              type="button"
              className={`kkp-distrib-map-chip ${active ? 'is-active' : ''}`}
              style={{ '--zone-color': zone.couleur || '#64748b' }}
              onClick={() => toggleZone(code)}
            >
              <span className="kkp-distrib-zone-dot" style={{ background: zone.couleur }} />
              {zone.label}
              <small>{zone.livreur || ''}</small>
            </button>
          );
        })}
      </div>

      <p className="text-secondary-light text-sm mb-2">
        {loading
          ? 'Chargement…'
          : `${geocodedCount} point(s) sur la carte · ${missingPoints.length} sans coordonnées`}
      </p>

      <div className="row g-3">
        <div className="col-lg-8">
          <KkpDistribMap points={visiblePoints} zoneColors={zoneColors} height={640} />
        </div>
        <div className="col-lg-4">
          <div className="card kkp-distrib-missing-card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <strong>Sans coordonnées</strong>
              <AppBadge variant="warning">{missingPoints.length}</AppBadge>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <p className="text-secondary-light text-sm p-3 mb-0">Chargement…</p>
              ) : missingPoints.length === 0 ? (
                <p className="text-secondary-light text-sm p-3 mb-0">Tous les points filtrés ont une position.</p>
              ) : (
                <ul className="kkp-distrib-missing-list">
                  {missingPoints.map((point) => (
                    <li key={point.id}>
                      <div className="kkp-distrib-missing-list__main">
                        <strong>{point.nom}</strong>
                        <span className="kkp-distrib-missing-list__meta">
                          <span className="kkp-distrib-zone-dot" style={{ background: point.zone_couleur || zoneColors[point.zone] }} />
                          {point.zone_label || point.zone}
                        </span>
                        <span className="kkp-distrib-missing-list__addr">{formatAddress(point)}</span>
                      </div>
                      <AppButton
                        size="sm"
                        variant="outline-primary"
                        onClick={() => setEditing(point)}
                      >
                        Placer
                      </AppButton>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      <KkpDistribPointModal
        show={Boolean(editing)}
        point={editing}
        zones={zones}
        initialTab="carte"
        saving={savingModal}
        onClose={() => setEditing(null)}
        onSubmit={handleSaveModal}
      />
    </MasterLayout>
  );
};

export default KkpDistribCartePage;
